import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  IconButton,
} from '@mui/material';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Refresh, Person, Circle, Engineering, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { journeyApi } from '../services/journeyApi';
import type { JourneyPhaseCode, TechnicianJourney, CustomerJourney } from '../types/technicianJourney';
import {
  buildTechnicianJourneyFlow,
  type JourneyPhaseNodeData,
  type JourneyPillarNodeData,
  type JourneyHubNodeData,
  type JourneyMetricNodeData,
} from './userJourney/buildJourneyFlow';
import { buildCustomerJourneyFlow } from './userJourney/buildCustomerJourneyFlow';
import { JourneyGraphInspector, JourneyUserSummaryCard } from './userJourney/journeyInspector';
import './UserJourneyManagement.css';

type JourneyMode = 'technician' | 'customer';

const PHASE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All phases' },
  { value: 'SIGNUP', label: 'Signup' },
  { value: 'PROFILE', label: 'Profile' },
  { value: 'APPROVAL', label: 'Approval' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'PORTFOLIO', label: 'Portfolio' },
  { value: 'COMPLETE', label: 'Complete' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const JourneyPhaseNode = memo(({ data }: NodeProps<Node<JourneyPhaseNodeData>>) => {
  const d = data;
  const cls = `uj-node uj-node--${d.variant}${d.active ? ' uj-node--pulse' : ''}`;
  const subCls = d.subtitle?.includes('\n') ? 'uj-node__sub uj-node__sub--multiline' : 'uj-node__sub';
  return (
    <div className={cls}>
      <Handle type="target" position={Position.Left} id="in" className="uj-handle" />
      <Handle type="source" position={Position.Right} id="out" className="uj-handle" />
      {d.ingressTop ? (
        <Handle type="target" position={Position.Top} id="from-hub" className="uj-handle" />
      ) : null}
      {d.branch ? (
        <Handle type="source" position={Position.Bottom} id="bottom" className="uj-handle uj-handle--branch" />
      ) : null}
      <div className="uj-node__title-row">
        {d.completed ? <span className="uj-node__check">✓</span> : <span className="uj-node__dot" />}
        <div>
          <div className="uj-node__label">{d.label}</div>
          <div className="uj-node__label-ar">{d.labelAr}</div>
        </div>
      </div>
      {d.subtitle ? <div className={subCls}>{d.subtitle}</div> : null}
    </div>
  );
});

const JourneyPillarNode = memo(({ data }: NodeProps<Node<JourneyPillarNodeData>>) => {
  const d = data;
  return (
    <div className={`uj-pillar ${d.completed ? 'uj-pillar--done' : ''}`}>
      <Handle type="target" position={Position.Top} id="top" className="uj-handle" />
      <div className="uj-pillar__label">{d.label}</div>
      <div className="uj-pillar__ar">{d.labelAr}</div>
      <div className="uj-pillar__detail">{d.detail}</div>
      {d.completed ? <span className="uj-pillar__ok">✓</span> : null}
    </div>
  );
});

const JourneyHubNode = memo(({ data }: NodeProps<Node<JourneyHubNodeData>>) => {
  const d = data;
  return (
    <div className="uj-hub">
      <Handle type="source" position={Position.Top} id="metrics" className="uj-handle" />
      <Handle type="source" position={Position.Bottom} id="pipeline" className="uj-handle" />
      <Handle type="source" position={Position.Right} id="out" className="uj-handle" />
      <div className="uj-hub__title">{d.title}</div>
      <div className="uj-hub__title-ar">{d.titleAr}</div>
      <ul className="uj-hub__lines">
        {d.lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
});

const JourneyMetricNode = memo(({ data }: NodeProps<Node<JourneyMetricNodeData>>) => {
  const d = data;
  return (
    <div className={`uj-metric uj-metric--${d.accent}`}>
      <Handle type="target" position={Position.Bottom} id="ing" className="uj-handle" />
      <Handle type="target" position={Position.Left} id="in" className="uj-handle" />
      {!d.chainEnd ? <Handle type="source" position={Position.Right} id="out" className="uj-handle" /> : null}
      <div className="uj-metric__label">{d.label}</div>
      <div className="uj-metric__label-ar">{d.labelAr}</div>
      <ul className="uj-metric__lines">
        {d.lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
});

const nodeTypes = {
  journeyPhase: JourneyPhaseNode,
  journeyPillar: JourneyPillarNode,
  journeyHub: JourneyHubNode,
  journeyMetric: JourneyMetricNode,
};

/** Refits the viewport when the flow container enters or exits fullscreen. */
const JourneyFitOnResize = memo(({ fullscreen }: { fullscreen: boolean }) => {
  const { fitView } = useReactFlow();
  const prevFs = useRef(fullscreen);
  useEffect(() => {
    if (prevFs.current === fullscreen) return;
    prevFs.current = fullscreen;
    const run = () => fitView({ duration: 250 });
    const raf = requestAnimationFrame(run);
    const t = window.setTimeout(run, 120);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [fullscreen, fitView]);
  return null;
});

const UserJourneyManagement: React.FC = () => {
  const [mode, setMode] = useState<JourneyMode>('technician');

  const [techStats, setTechStats] = useState<Awaited<ReturnType<typeof journeyApi.technicianStatsOrMock>>['stats'] | null>(
    null
  );
  const [techStatsMock, setTechStatsMock] = useState(false);
  const [custStats, setCustStats] = useState<Awaited<ReturnType<typeof journeyApi.customerStatsOrMock>>['stats'] | null>(
    null
  );
  const [custStatsMock, setCustStatsMock] = useState(false);

  const [techJourneys, setTechJourneys] = useState<TechnicianJourney[]>([]);
  const [techListMock, setTechListMock] = useState(false);
  const [custJourneys, setCustJourneys] = useState<CustomerJourney[]>([]);
  const [custListMock, setCustListMock] = useState(false);

  const [phaseFilter, setPhaseFilter] = useState('');
  const [techSelectedId, setTechSelectedId] = useState<number | null>(null);
  const [custSelectedId, setCustSelectedId] = useState<number | null>(null);
  const [selectedTech, setSelectedTech] = useState<TechnicianJourney | null>(null);
  const [selectedCust, setSelectedCust] = useState<CustomerJourney | null>(null);
  const [techDetailMock, setTechDetailMock] = useState(false);
  const [custDetailMock, setCustDetailMock] = useState(false);

  const [loadingTechList, setLoadingTechList] = useState(true);
  const [loadingCustList, setLoadingCustList] = useState(false);
  const [loadingTechDetail, setLoadingTechDetail] = useState(false);
  const [loadingCustDetail, setLoadingCustDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedGraphNodeId, setSelectedGraphNodeId] = useState<string | null>(null);
  const flowFullscreenRef = useRef<HTMLDivElement | null>(null);
  const [isFlowFullscreen, setIsFlowFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => {
      const el = document.fullscreenElement;
      setIsFlowFullscreen(el === flowFullscreenRef.current && el != null);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFlowFullscreen = useCallback(async () => {
    const el = flowFullscreenRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      /* Safari / blocked fullscreen */
    }
  }, []);

  const loadTechStats = useCallback(async () => {
    const { stats: s, fromMock } = await journeyApi.technicianStatsOrMock();
    setTechStats(s);
    setTechStatsMock(fromMock);
  }, []);

  const loadCustStats = useCallback(async () => {
    const { stats: s, fromMock } = await journeyApi.customerStatsOrMock();
    setCustStats(s);
    setCustStatsMock(fromMock);
  }, []);

  const loadTechList = useCallback(async () => {
    setLoadingTechList(true);
    setError(null);
    try {
      const { journeys: list, fromMock } = await journeyApi.listTechniciansOrMock(phaseFilter || undefined);
      setTechJourneys(list);
      setTechListMock(fromMock);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load technician journeys');
    } finally {
      setLoadingTechList(false);
    }
  }, [phaseFilter]);

  const loadCustList = useCallback(async () => {
    setLoadingCustList(true);
    setError(null);
    try {
      const { journeys: list, fromMock } = await journeyApi.listCustomersOrMock();
      setCustJourneys(list);
      setCustListMock(fromMock);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load customer journeys');
    } finally {
      setLoadingCustList(false);
    }
  }, []);

  const loadTechDetail = useCallback(async (id: number) => {
    setLoadingTechDetail(true);
    try {
      const { journey, fromMock } = await journeyApi.getTechnicianJourneyOrMock(id);
      setSelectedTech(journey);
      setTechDetailMock(fromMock);
    } finally {
      setLoadingTechDetail(false);
    }
  }, []);

  const loadCustDetail = useCallback(async (id: number) => {
    setLoadingCustDetail(true);
    try {
      const { journey, fromMock } = await journeyApi.getCustomerJourneyOrMock(id);
      setSelectedCust(journey);
      setCustDetailMock(fromMock);
    } finally {
      setLoadingCustDetail(false);
    }
  }, []);

  useEffect(() => {
    loadTechStats();
    loadCustStats();
  }, [loadTechStats, loadCustStats]);

  useEffect(() => {
    loadTechList();
  }, [loadTechList]);

  useEffect(() => {
    if (mode === 'customer') loadCustList();
  }, [mode, loadCustList]);

  useEffect(() => {
    if (mode !== 'technician' || techJourneys.length === 0) return;
    if (techSelectedId == null) setTechSelectedId(techJourneys[0].technicianId);
    else if (!techJourneys.some((j) => j.technicianId === techSelectedId)) {
      setTechSelectedId(techJourneys[0].technicianId);
    }
  }, [mode, techJourneys, techSelectedId]);

  useEffect(() => {
    if (mode !== 'customer' || custJourneys.length === 0) return;
    if (custSelectedId == null) setCustSelectedId(custJourneys[0].customerId);
    else if (!custJourneys.some((j) => j.customerId === custSelectedId)) {
      setCustSelectedId(custJourneys[0].customerId);
    }
  }, [mode, custJourneys, custSelectedId]);

  useEffect(() => {
    if (mode !== 'technician' || techSelectedId == null) return;
    loadTechDetail(techSelectedId);
  }, [mode, techSelectedId, loadTechDetail]);

  useEffect(() => {
    if (mode !== 'customer' || custSelectedId == null) return;
    loadCustDetail(custSelectedId);
  }, [mode, custSelectedId, loadCustDetail]);

  const flowKey = mode === 'technician' ? selectedTech : selectedCust;
  useEffect(() => {
    setSelectedGraphNodeId(null);
    if (mode === 'technician') {
      if (!selectedTech) {
        setNodes([]);
        setEdges([]);
        return;
      }
      const { nodes: n, edges: e } = buildTechnicianJourneyFlow(selectedTech);
      setNodes(n as Node[]);
      setEdges(e);
    } else {
      if (!selectedCust) {
        setNodes([]);
        setEdges([]);
        return;
      }
      const { nodes: n, edges: e } = buildCustomerJourneyFlow(selectedCust);
      setNodes(n as Node[]);
      setEdges(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setNodes/setEdges from useNodesState are unstable; only mode + selected journey should rebuild
  }, [mode, flowKey]);

  const onGraphNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedGraphNodeId(node.id);
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === node.id })));
    },
    [setNodes]
  );

  const onGraphPaneClick = useCallback(() => {
    setSelectedGraphNodeId(null);
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
  }, [setNodes]);

  const selectedGraphNode = useMemo(
    () => (selectedGraphNodeId ? (nodes.find((n) => n.id === selectedGraphNodeId) ?? null) : null),
    [nodes, selectedGraphNodeId]
  );

  const refresh = useCallback(() => {
    loadTechStats();
    loadCustStats();
    loadTechList();
    if (mode === 'customer') loadCustList();
    if (mode === 'technician' && techSelectedId != null) loadTechDetail(techSelectedId);
    if (mode === 'customer' && custSelectedId != null) loadCustDetail(custSelectedId);
  }, [
    loadTechStats,
    loadCustStats,
    loadTechList,
    loadCustList,
    loadTechDetail,
    loadCustDetail,
    mode,
    techSelectedId,
    custSelectedId,
  ]);

  const showMockBanner =
    (mode === 'technician' && (techStatsMock || techListMock || techDetailMock)) ||
    (mode === 'customer' && (custStatsMock || custListMock || custDetailMock));

  const phaseChips = useMemo(() => {
    if (mode !== 'technician' || !techStats?.phaseCounts) return null;
    const order: JourneyPhaseCode[] = [
      'SIGNUP',
      'PROFILE',
      'APPROVAL',
      'ONBOARDING',
      'PORTFOLIO',
      'COMPLETE',
      'SUSPENDED',
    ];
    return order.map((key) => (
      <Chip
        key={key}
        size="small"
        label={`${key}: ${techStats.phaseCounts[key] ?? 0}`}
        sx={{ mr: 0.5, mb: 0.5, fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}
      />
    ));
  }, [mode, techStats]);

  const loadingList = mode === 'technician' ? loadingTechList : loadingCustList;
  const loadingDetail = mode === 'technician' ? loadingTechDetail : loadingCustDetail;
  const selected = mode === 'technician' ? selectedTech : selectedCust;

  return (
    <Box className="user-journey-page">
      <Box className="uj-header">
        <Typography variant="h5" fontWeight={800} sx={{ color: 'var(--text-primary)' }}>
          User journey
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
          Technician and customer lifecycles — click any graph card to inspect full details on the right
        </Typography>
      </Box>

      <ToggleButtonGroup
        className="uj-mode-toggle"
        value={mode}
        exclusive
        size="small"
        onChange={(_, v) => {
          if (v) setMode(v);
        }}
        aria-label="Journey mode"
      >
        <ToggleButton value="technician">
          <Engineering sx={{ fontSize: 18, mr: 0.75 }} />
          Technicians
        </ToggleButton>
        <ToggleButton value="customer">
          <Person sx={{ fontSize: 18, mr: 0.75 }} />
          Customers
        </ToggleButton>
      </ToggleButtonGroup>

      {showMockBanner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing <strong>demo or cached data</strong> because the journey API is unreachable or returned no rows.
          With a valid JWT and permissions (
          {mode === 'technician' ? (
            <code>ADMIN_TECHNICIAN_JOURNEY_*</code>
          ) : (
            <code>ADMIN_CUSTOMER_JOURNEY_*</code>
          )}
          ), live data loads automatically.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper className="uj-stats-bar" elevation={0}>
        {mode === 'technician' ? (
          techStats ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                <strong>{techStats.totalTechnicians}</strong> technicians
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Avg progress: <strong>{techStats.averageProgress}%</strong>
              </Typography>
              <Box sx={{ flex: 1, minWidth: 200 }}>{phaseChips}</Box>
              <Button startIcon={<Refresh />} size="small" onClick={refresh}>
                Refresh
              </Button>
            </Box>
          ) : (
            <LinearProgress />
          )
        ) : custStats ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              <strong>{custStats.totalCustomers}</strong> customers
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              Avg projects (30d): <strong>{custStats.averageProjectsLast30Days}</strong>
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button startIcon={<Refresh />} size="small" onClick={refresh}>
              Refresh
            </Button>
          </Box>
        ) : (
          <LinearProgress />
        )}
      </Paper>

      <Box className="uj-main-grid">
        <Paper className="uj-side" elevation={0}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            {mode === 'technician' ? 'Technicians' : 'Customers'}
          </Typography>
          {mode === 'technician' ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Filter by phase</InputLabel>
              <Select
                value={phaseFilter}
                label="Filter by phase"
                onChange={(e) => {
                  setPhaseFilter(e.target.value);
                  setTechSelectedId(null);
                }}
              >
                {PHASE_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : mode === 'technician' ? (
            <Box className="uj-tech-list">
              {techJourneys.map((j) => (
                <button
                  key={j.technicianId}
                  type="button"
                  className={`uj-tech-item ${techSelectedId === j.technicianId ? 'active' : ''}`}
                  onClick={() => setTechSelectedId(j.technicianId)}
                >
                  <Person sx={{ fontSize: 18, opacity: 0.7 }} />
                  <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <div className="uj-tech-name">{j.name}</div>
                    <div className="uj-tech-meta">
                      #{j.technicianId} · {j.currentPhase} · {j.overallProgress}%
                    </div>
                  </Box>
                  <Tooltip title={j.isOnline ? 'Online' : 'Offline'}>
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: j.isOnline ? 'var(--success-500, #22c55e)' : 'var(--text-muted)',
                      }}
                    />
                  </Tooltip>
                </button>
              ))}
              {techJourneys.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No technicians in this filter.
                </Typography>
              )}
            </Box>
          ) : (
            <Box className="uj-tech-list">
              {custJourneys.map((j) => (
                <button
                  key={j.customerId}
                  type="button"
                  className={`uj-tech-item ${custSelectedId === j.customerId ? 'active' : ''}`}
                  onClick={() => setCustSelectedId(j.customerId)}
                >
                  <Person sx={{ fontSize: 18, opacity: 0.7 }} />
                  <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <div className="uj-tech-name">{j.name}</div>
                    <div className="uj-tech-meta">
                      #{j.customerId} · {j.status} · {j.totalProjectsPosted} projects
                    </div>
                  </Box>
                </button>
              ))}
              {custJourneys.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No customers in the list.
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        <Paper className="uj-canvas-wrap" elevation={0}>
          {loadingDetail || !selected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
              {loadingDetail ? (
                <CircularProgress />
              ) : (
                <Typography color="text.secondary">
                  Select a {mode === 'technician' ? 'technician' : 'customer'}
                </Typography>
              )}
            </Box>
          ) : (
            <div ref={flowFullscreenRef} className="uj-flow-fullscreen-target">
              <Box
                className="uj-canvas-head"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  {mode === 'technician' && selectedTech ? (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedTech.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedTech.phoneNumber}
                        {selectedTech.email ? ` · ${selectedTech.email}` : ''}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, selectedTech.overallProgress))}
                        sx={{ mt: 1, height: 8, borderRadius: 1 }}
                      />
                    </>
                  ) : selectedCust ? (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedCust.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCust.phoneNumber}
                        {selectedCust.email ? ` · ${selectedCust.email}` : ''}
                      </Typography>
                    </>
                  ) : null}
                </Box>
                <Tooltip title={isFlowFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen diagram'}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={toggleFlowFullscreen}
                    aria-label={isFlowFullscreen ? 'Exit fullscreen' : 'Fullscreen diagram'}
                    sx={{
                      flexShrink: 0,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    {isFlowFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                </Tooltip>
              </Box>
              <div className="uj-flow-host">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onGraphNodeClick}
                  onPaneClick={onGraphPaneClick}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: mode === 'technician' ? 0.2 : 0.15 }}
                  minZoom={0.2}
                  maxZoom={1.5}
                  proOptions={{ hideAttribution: true }}
                >
                  <JourneyFitOnResize fullscreen={isFlowFullscreen} />
                  <Background gap={20} size={1} color="var(--border-light, #e2e8f0)" />
                  <Controls showInteractive={false} />
                </ReactFlow>
              </div>
            </div>
          )}
        </Paper>

        {mode === 'technician' && selectedTech && (
          <Paper className="uj-detail uj-detail--panel" elevation={0}>
            <Stack spacing={0}>
              <Typography variant="overline" fontWeight={800} letterSpacing={1} color="text.secondary" sx={{ mb: 0.5 }}>
                Support inspector
              </Typography>
              <JourneyUserSummaryCard mode="technician" technician={selectedTech} customer={null} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Node details
              </Typography>
              <JourneyGraphInspector
                mode="technician"
                selectedNode={selectedGraphNode}
                technician={selectedTech}
                customer={null}
              />
            </Stack>
          </Paper>
        )}

        {mode === 'customer' && selectedCust && (
          <Paper className="uj-detail uj-detail--panel" elevation={0}>
            <Stack spacing={0}>
              <Typography variant="overline" fontWeight={800} letterSpacing={1} color="text.secondary" sx={{ mb: 0.5 }}>
                Support inspector
              </Typography>
              <JourneyUserSummaryCard mode="customer" technician={null} customer={selectedCust} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Node details
              </Typography>
              <JourneyGraphInspector
                mode="customer"
                selectedNode={selectedGraphNode}
                technician={null}
                customer={selectedCust}
              />
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default UserJourneyManagement;
